'use client';

import { TableCell, TableRow } from '../components';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { LinkCell } from './LinkCell';
import type { ColumnDef } from '@tanstack/react-table';
const isDev = process.env.NODE_ENV === 'development';
const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();
export interface ChildRowsProps<TChildRow, TParentRow> {
  childRows: TChildRow[];
  childColumns: ColumnDef<TChildRow, unknown>[];
  leadingCellCount: number;
  parentColumnCount: number;
  inset: boolean;
  parentRow: TParentRow;
  onChildRowClick?: (childRow: TChildRow, parentRow: TParentRow) => void;
  childRowHref?: (childRow: TChildRow, parentRow: TParentRow) => string;
}

/**
 * Child rows get their own table instance purely to build cells from
 * `childColumns`: sub-rows have their own shape, so they can't reuse the
 * parent's cells, and TanStack only ever renders a row against the parent
 * column set.
 *
 * A clickable/linked child row mirrors the parent body's handling: the whole
 * row hovers and clicks, a child column marked `meta.ownsCellClicks` swallows
 * clicks over its own controls, and `childRowHref` wraps each cell in a link.
 */
export function ChildRows<TChildRow, TParentRow>({
  childRows,
  childColumns,
  leadingCellCount,
  parentColumnCount,
  inset,
  parentRow,
  onChildRowClick,
  childRowHref
}: ChildRowsProps<TChildRow, TParentRow>) {
  const table = useReactTable({
    data: childRows,
    columns: childColumns,
    getCoreRowModel: getCoreRowModel()
  });
  const childCellCount = childColumns.length + leadingCellCount;
  if (isDev && childCellCount > parentColumnCount) {
    // Silently overflowing the parent grid misaligns every column instead of pointing at
    // the config that caused it.
    console.warn(`rowExpansion.childColumns declares ${childColumns.length} columns, which does not fit the ` + `parent table's ${parentColumnCount} (${leadingCellCount} taken by the checkbox/chevron). ` + `Child rows will overflow the parent's grid.`);
  }
  return <>
      {table.getRowModel().rows.map(childRow => {
      const cells = childRow.getVisibleCells();
      const fillerSpan = Math.max(parentColumnCount - leadingCellCount - cells.length, 0);
      const href = childRowHref?.(childRow.original, parentRow);
      const handleClick = onChildRowClick ? () => onChildRowClick(childRow.original, parentRow) : undefined;
      const interactive = !!handleClick || !!href;
      // Grid position across the whole row (leading indent + content + filler), so a linked
      // child is clickable edge-to-edge, not just over its content cells, and the inset
      // first/last padding lands on the true outer cells.
      const lastCellIndex = leadingCellCount + cells.length + (fillerSpan > 0 ? 1 : 0) - 1;
      const linkCell = (node: React.ReactNode, cellIndex: number) => href ? <LinkCell href={href} inset={inset} isFirst={cellIndex === 0} isLast={cellIndex === lastCellIndex}>
              {node}
            </LinkCell> : node;
      return <TableRow
      // A shade lighter than a full secondary fill, so the rowHoverable hover (full
      // background-secondary) reads as a darker hover, with a left border bar marking the
      // nested group. Text stays default (dark), not muted.
      className="bg-background-secondary/45 shadow-[inset_2px_0_0_0_var(--color-border-secondary)]" key={childRow.id} onClick={handleClick} rowHoverable={interactive}
      // A click handler alone leaves the row mouse-only; make it a real button for the
      // keyboard. (childRowHref rows are real anchors, already keyboard-accessible.)
      {...handleClick && {
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleClick();
          }
        },
        role: 'button',
        tabIndex: 0
      }}>
            {Array.from({
          length: leadingCellCount
        }).map((_, index) => <TableCell inset={inset} key={`lead-${index}`}>
                {linkCell(null, index)}
              </TableCell>)}
            {cells.map((cell, index_0) => {
          const content = flexRender(cell.column.columnDef.cell, cell.getContext());
          const columnMeta = cell.column.columnDef.meta;
          const ownsClicks = !!columnMeta?.ownsCellClicks;
          return <TableCell inset={inset} key={cell.id}
          // Padding around the cell's own control is still part of the cell, so the
          // whole cell has to swallow the click, not just the control inside it.
          onClick={ownsClicks ? stopPropagation : undefined} style={columnMeta?.style}>
                  {ownsClicks ? content : linkCell(content, leadingCellCount + index_0)}
                </TableCell>;
        })}
            {fillerSpan > 0 && <TableCell colSpan={fillerSpan} inset={inset}>
                {linkCell(null, lastCellIndex)}
              </TableCell>}
          </TableRow>;
    })}
    </>;
}